/**
 * Web location source.
 *
 * Three of expo-location's web behaviours differ from native in ways that matter
 * to the caller, which is why this is a separate file rather than a branch:
 *
 * 1. `getPermissionsAsync()` throws `UnavailabilityError` when
 *    `navigator.permissions.query` is missing -- that is "unknown", not "denied".
 * 2. `hasServicesEnabledAsync()` reports whether the browser has a geolocation
 *    API at all, not whether the user switched GPS off. Different copy.
 * 3. `Linking.openSettings()` is not implemented and throws.
 *
 * @module utils/location-source
 */

import * as Location from 'expo-location';
import {
  Coords,
  LAST_KNOWN_MAX_AGE_MS,
  LAST_KNOWN_REQUIRED_ACCURACY_M,
  LocationPermissionState,
  ServicesDisabledCopy,
} from './location-source.types';

function toCoords(location: Location.LocationObject | null): Coords | null {
  if (!location) return null;
  return {
    latitude: location.coords.latitude,
    longitude: location.coords.longitude,
  };
}

function toPermissionState(status: string): LocationPermissionState {
  if (status === 'granted') return 'granted';
  if (status === 'denied') return 'denied';
  return 'undetermined';
}

/**
 * Reads the current permission without prompting.
 *
 * A browser that cannot answer a permissions query reports `undetermined`, never
 * `denied` -- the caller would otherwise tell the user they refused access they
 * were never asked for.
 */
export async function getPermissionState(): Promise<LocationPermissionState> {
  try {
    const { status } = await Location.getForegroundPermissionsAsync();
    return toPermissionState(status);
  } catch {
    return 'undetermined';
  }
}

export async function requestPermission(): Promise<LocationPermissionState> {
  const { status } = await Location.requestForegroundPermissionsAsync();
  return toPermissionState(status);
}

/** On web this reports whether the browser exposes a geolocation API at all. */
export async function areServicesEnabled(): Promise<boolean> {
  try {
    return await Location.hasServicesEnabledAsync();
  } catch {
    return false;
  }
}

export async function getLastKnownCoords(): Promise<Coords | null> {
  try {
    const location = await Location.getLastKnownPositionAsync({
      maxAge: LAST_KNOWN_MAX_AGE_MS,
      requiredAccuracy: LAST_KNOWN_REQUIRED_ACCURACY_M,
    });
    return toCoords(location);
  } catch {
    return null;
  }
}

/** A fresh fix, or null if none arrives within `timeoutMs`. */
export async function getFreshCoords(timeoutMs: number): Promise<Coords | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;

  // `maximumAge` lets a recently-cached browser position return instantly, and
  // `timeout` self-bounds the call so it gives up on its own rather than hanging
  // while the geolocation subsystem is still cold. Both are standard browser
  // PositionOptions that expo-location's web layer forwards verbatim to
  // `navigator.geolocation`, but they are absent from the React Native
  // `LocationOptions` type -- hence the cast. The outer `Promise.race` timer stays
  // as a safety net in case the browser ignores its own `timeout`.
  const positionOptions = {
    accuracy: Location.Accuracy.Balanced,
    maximumAge: LAST_KNOWN_MAX_AGE_MS,
    timeout: timeoutMs,
  } as Location.LocationOptions;

  // Attached synchronously so a late rejection is never unhandled.
  const fresh = Location.getCurrentPositionAsync(positionOptions)
    .then(toCoords)
    .catch(() => null);

  const expiry = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), timeoutMs);
  });

  try {
    return await Promise.race([fresh, expiry]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** `Linking.openSettings()` is not implemented on web -- it throws if called. */
export const canOpenSettings = false;

export async function openLocationSettings(): Promise<void> {
  // No browser equivalent; the caller hides the affordance via `canOpenSettings`.
}

export const servicesDisabledCopy: ServicesDisabledCopy = {
  title: "Location isn't available",
  body: 'This browser does not support location.',
};

// Re-exported from this platform module so the engine reads it through the same
// platform-resolved `@/utils/location-source` surface it reads the functions from.
// (Metro resolves that specifier to THIS file on web, so a value exported only
// from the base barrel would never reach the engine here.)
export { LOCATION_TIMEOUT_MS } from './location-source.types';

/**
 * Web overrides the default single-attempt policy with a patient retry.
 *
 * A browser has no last-known cache to paint from, and its geolocation subsystem
 * is cold on the first page load, so the very first fresh call routinely times
 * out. Three spaced attempts bridge the warm-up: the engine stays in its spinner
 * between them and only reports the location unavailable once all are exhausted.
 */
export const FRESH_FIX_ATTEMPTS = 3;
export const FRESH_FIX_RETRY_DELAY_MS = 800;
